import { createContext, useContext, useState, useEffect } from 'react';

// Riester Design System — red/yellow-only accent, Aeonik, zero-radius.
// Every key name below is preserved from the pre-RDS palette so every
// consumer keeps compiling unchanged; only values move. `purple` is
// retired to a neutral gray (RDS has no purple) rather than deleted, so
// any consumer degrades gracefully instead of breaking outright — real
// consumers (HeaderBar's Riester pill, Brand Kits icon) are fixed
// directly in their own files this same pass; the team page's old
// ROLE_COLORS map was retired outright in the Sub-phase D pass in favor
// of a plain accent/neutral badge. `red` now
// equals RDS's brand red exactly, matching the design system's own
// choice to use one red for both the light-mode accent and the
// destructive/danger color (see colors_and_type.css's
// --default-action-link-color: var(--red)). `green`/`orange` are left
// as-is — they're pre-existing status semantics (success/warning), not
// brand-accent colors, and outside this pass's explicit scope.
const dark = {
  name: 'dark',
  bg: '#000000',
  bgSecondary: 'rgba(255,255,255,0.02)',
  bgTertiary: 'rgba(255,255,255,0.03)',
  bgHover: 'rgba(255,255,255,0.04)',
  bgElevated: '#121212',
  bgInput: 'rgba(255,255,255,0.05)',
  text: '#F5F5F5',
  textSecondary: 'rgba(245,245,245,0.7)',
  textTertiary: 'rgba(245,245,245,0.5)',
  textMuted: 'rgba(245,245,245,0.4)',
  textFaint: 'rgba(245,245,245,0.3)',
  textGhost: 'rgba(245,245,245,0.25)',
  textDisabled: 'rgba(245,245,245,0.2)',
  border: '#2A2A2C',
  borderLight: '#1C1C1E',
  borderMedium: '#58585B',
  borderHover: 'rgba(255,255,255,0.2)',
  accent: '#FFFF00',
  accentFg: '#000000',
  accentBg: 'rgba(255,255,0,0.1)',
  accentBorder: 'rgba(255,255,0,0.2)',
  accentBgHover: 'rgba(255,255,0,0.15)',
  accentBorderHover: 'rgba(255,255,0,0.3)',
  fill: '#FFFFFF',
  fillFg: '#000000',
  purple: '#8A8A8D',
  purpleBg: 'rgba(255,255,255,0.06)',
  green: '#45B26B',
  greenBg: 'rgba(69,178,107,0.12)',
  greenBorder: 'rgba(69,178,107,0.3)',
  red: '#FF0000',
  redBg: 'rgba(255,0,0,0.12)',
  redBorder: 'rgba(255,0,0,0.3)',
  orange: '#F2994A',
  shadow: '0 8px 24px rgba(0,0,0,0.5)',
  scrollbarThumb: 'rgba(255,255,255,0.08)',
  scrollbarHover: 'rgba(255,255,255,0.15)',
  selectBg: '#121212',
  cardBg: 'rgba(255,255,255,0.02)',
  cardBorder: '#1C1C1E',
  cardBgHover: 'rgba(255,255,255,0.04)',
  cardBorderHover: '#2A2A2C',
  headerBg: 'rgba(0,0,0,0.95)',
  badgeBg: 'rgba(255,255,255,0.05)',
  badgeText: 'rgba(245,245,245,0.35)',
  overlay: 'rgba(0,0,0,0.6)',
};

const light = {
  name: 'light',
  bg: '#FFFFFF',
  bgSecondary: 'rgba(0,0,0,0.02)',
  bgTertiary: 'rgba(0,0,0,0.03)',
  bgHover: 'rgba(0,0,0,0.04)',
  bgElevated: '#FAFAFA',
  bgInput: 'rgba(0,0,0,0.04)',
  text: '#1A1A1C',
  textSecondary: 'rgba(26,26,28,0.75)',
  textTertiary: 'rgba(26,26,28,0.55)',
  textMuted: 'rgba(26,26,28,0.45)',
  textFaint: 'rgba(26,26,28,0.35)',
  textGhost: 'rgba(26,26,28,0.25)',
  textDisabled: 'rgba(26,26,28,0.2)',
  border: '#D9D9D9',
  borderLight: '#ECECEC',
  borderMedium: '#B3B3B3',
  borderHover: 'rgba(0,0,0,0.25)',
  accent: '#FF0000',
  accentFg: '#FFFFFF',
  accentBg: 'rgba(255,0,0,0.08)',
  accentBorder: 'rgba(255,0,0,0.2)',
  accentBgHover: 'rgba(255,0,0,0.12)',
  accentBorderHover: 'rgba(255,0,0,0.3)',
  fill: '#000000',
  fillFg: '#FFFFFF',
  purple: '#58585B',
  purpleBg: 'rgba(0,0,0,0.05)',
  green: '#2D9950',
  greenBg: 'rgba(45,153,80,0.08)',
  greenBorder: 'rgba(45,153,80,0.25)',
  red: '#FF0000',
  redBg: 'rgba(255,0,0,0.08)',
  redBorder: 'rgba(255,0,0,0.2)',
  orange: '#D97B2A',
  shadow: '0 8px 24px rgba(0,0,0,0.1)',
  scrollbarThumb: 'rgba(0,0,0,0.12)',
  scrollbarHover: 'rgba(0,0,0,0.2)',
  selectBg: '#FFFFFF',
  cardBg: '#FFFFFF',
  cardBorder: '#ECECEC',
  cardBgHover: 'rgba(0,0,0,0.02)',
  cardBorderHover: '#D9D9D9',
  headerBg: 'rgba(255,255,255,0.95)',
  badgeBg: 'rgba(0,0,0,0.06)',
  badgeText: 'rgba(26,26,28,0.45)',
  overlay: 'rgba(0,0,0,0.3)',
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('fontvault_theme');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    localStorage.setItem('fontvault_theme', isDark ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const theme = isDark ? dark : light;
  const toggle = () => setIsDark(p => !p);

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
